import {
  SkyButton,
  SkyDataGrid,
  SkyText,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { useMemo } from "react";
import { useLocation } from "wouter";
import type { EgressView } from "@shared/types/settings";
import {
  EmptyState,
  ErrorState,
  GridSkeleton,
  ScreenNote,
} from "@/components/platform/ScreenState";
import { useEgressView } from "@/hooks/use-platform-settings";
import { CopyIcon, DownloadIcon } from "@/lib/icons";
import { ROUTES, absolutePath } from "@/lib/routes";
import { useNotice } from "@/lib/feedback/NoticeProvider";

// copy-jargon-file-ok: an operator setting: the hosts the runner may reach. (#929, scripts/check-copy.mjs)

/**
 * The egress allow-list (FR-72, canvas 14 panel 03).
 *
 * Computed, never edited: it is the union of `egress_fqdns` over every ACTIVE
 * connector definition, and the firewall rule for connector hosts is generated
 * from it by the release pipeline. The tab says "computed, not edited" before the reader
 * starts hunting for a save button, because a read-only grid that looks exactly
 * like an editable one is a small cruelty repeated every visit.
 *
 * A definition that declares no hosts is UNRESTRICTED, which is a different
 * statement from "declares nothing", so those are named separately rather than
 * left out of a list whose whole purpose is completeness.
 */

interface EgressRow {
  id: string;
  fqdn: string;
  connector: string;
}

export function EgressTab() {
  const notice = useNotice();
  const [, navigate] = useLocation();
  const egress = useEgressView();

  const rows = useMemo<EgressRow[]>(() => {
    const view = egress.data;
    if (!view) return [];
    return view.connectors
      .flatMap((connector) =>
        connector.fqdns.map((fqdn) => ({
          id: `${connector.name}:${fqdn}`,
          fqdn,
          connector: connector.name,
        })),
      )
      .sort((a, b) => a.fqdn.localeCompare(b.fqdn) || a.connector.localeCompare(b.connector));
  }, [egress.data]);

  const columns: SkyDataGridColumn<EgressRow>[] = [
    {
      key: "fqdn",
      header: "FQDN",
      sortable: true,
      width: "58%",
      renderCell: (_value, row) => (
        <SkyText size={200} font="monospace" wrap>
          {row.fqdn}
        </SkyText>
      ),
    },
    {
      key: "connector",
      header: "Required by",
      sortable: true,
      width: "42%",
      renderCell: (_value, row) => (
        <SkyText size={200} font="monospace">
          {row.connector}
        </SkyText>
      ),
    },
  ];

  if (egress.isPending) {
    return <GridSkeleton rows={5} label="Computing the allow-list…" testId="egress-loading" />;
  }

  if (egress.isError) {
    return (
      <ErrorState
        title="The egress allow-list could not be computed"
        error={egress.error}
        onRetry={() => void egress.refetch()}
        testId="egress-error"
      />
    );
  }

  const view = egress.data;

  return (
    <div className="flex flex-col gap-4" data-testid="egress-tab">
      <ScreenNote
        title="This list is computed, not edited"
        message="It is the union of the egress FQDNs on every active connector definition. Change a definition and this list changes with it. The firewall rule for connector hosts is generated from it by the release pipeline."
      />

      {rows.length === 0 ? (
        <EmptyState
          testId="egress-empty"
          title="No egress hosts yet"
          message="The allow-list is computed from active connector definitions. Seed a connector and the hosts it needs appear here."
          // Absolute: this tab renders inside the nested
          // /admin/platform-settings router, so a bare path would resolve
          // against that section's base and match nothing.
          action={
            <SkyButton
              appearance="secondary"
              onClick={() => navigate(absolutePath(ROUTES.adminConnectors))}
            >
              Open connectors
            </SkyButton>
          }
        />
      ) : (
        <>
          <div className="flex items-center gap-2">
            <SkyButton
              appearance="subtle"
              size="small"
              icon={CopyIcon}
              testId="egress-copy-json"
              onClick={() => {
                void navigator.clipboard
                  ?.writeText(JSON.stringify(view?.fqdns ?? [], null, 2))
                  .then(() => notice.success("Allow-list copied as JSON"))
                  .catch(() => {
                    /* insecure origin — the table is on screen */
                  });
              }}
            >
              Copy as JSON
            </SkyButton>
            <SkyButton
              appearance="subtle"
              size="small"
              icon={DownloadIcon}
              testId="egress-copy-csv"
              onClick={() => {
                const csv = [
                  "fqdn,connector",
                  ...rows.map((row) => `${row.fqdn},${row.connector}`),
                ].join("\n");
                void navigator.clipboard
                  ?.writeText(csv)
                  .then(() => notice.success("Allow-list copied as CSV"))
                  .catch(() => {
                    /* insecure origin — the table is on screen */
                  });
              }}
            >
              Copy as CSV
            </SkyButton>
          </div>

          <SkyDataGrid<EgressRow>
            data={rows}
            columns={columns}
            getRowId={(row) => row.id}
            testId="egress-grid"
          />

          <SkyText size={200} color="muted" block>
            {view?.fqdns.length ?? 0} distinct hosts from {view?.connectors.length ?? 0} active
            connector definitions.
          </SkyText>
        </>
      )}

      {view && view.unrestricted.length > 0 ? (
        <SkyText size={200} color="muted" block>
          Unrestricted, because they declare no hosts: {view.unrestricted.join(", ")}. A definition
          with an empty list is not blocked by this allow-list. It is not constrained by it either.
        </SkyText>
      ) : null}

      {view?.platform && view.platform.length > 0 ? (
        <NotificationHosts platform={view.platform} hasConnectorRows={rows.length > 0} />
      ) : null}
    </div>
  );
}

/**
 * The hosts the RUNNER posts notifications to (R17). They come from the release, not from a
 * connector definition, so they sit apart from the grid and from the copied union: the copy
 * buttons stay the connector list they always were. The release's printed allow-list names them
 * under "notifications" (`formatNotificationHosts` in `release/release.ts`).
 * A server without the notification module sends no `platform`, and nothing renders here.
 */
function NotificationHosts({
  platform,
  hasConnectorRows,
}: {
  platform: NonNullable<EgressView["platform"]>;
  /** The copy buttons render only beside connector rows, so only then does the note name them. */
  hasConnectorRows: boolean;
}) {
  return (
    <section
      className="flex flex-col gap-2"
      aria-labelledby="egress-platform-heading"
      data-testid="egress-platform"
    >
      <SkyText as="h2" id="egress-platform-heading" size={400} weight="semibold" block>
        Notifications
      </SkyText>
      <SkyText size={200} color="muted" block testId="egress-platform-note">
        The runner posts notifications to these hosts on port 443. They are set by this release, not
        by a connector definition. The allow-list the release prints lists them under
        &quot;notifications&quot;
        {hasConnectorRows ? ", but Copy as JSON and Copy as CSV leave them out" : ""}. Add them to
        any firewall rule you build.
      </SkyText>
      <ul className="flex flex-col gap-1">
        {platform.map((entry) => (
          <li key={entry.purpose} data-testid={`egress-platform-${entry.purpose}`}>
            <SkyText size={200}>{entry.label}: </SkyText>
            <SkyText size={200} font="monospace">
              {entry.fqdns.join(", ")}
            </SkyText>
            {entry.configured ? null : (
              <SkyText size={200} color="muted" block>
                {entry.purpose === "email"
                  ? "Not in use yet: email is not set up for this deployment."
                  : "Not in use yet."}
              </SkyText>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
